import { useEffect, useState } from "react";
import { BodySection } from "@/components/ui/BodySection";
import { ViewAllLink } from "@/components/ui/ViewAllLink";
import { merchantTimelinePath } from "@/routes";
import {
  fetchMerchantDebugContext,
  type TimelineEntry,
} from "@/application/merchants/debug_context_repository";

import { MerchantTimeline } from "./MerchantTimeline";

/** Enough to see what just happened; the rest is on the timeline page. */
const PREVIEW_LIMIT = 5;

/**
 * The most recent things that happened to this merchant.
 *
 * Asks the backend for only what it shows: the query caps the merge, so the
 * five entries here cost the same fan-out as the full page but carry a
 * fraction of the payload.
 */
export function AuditLogSection({ businessId }: { businessId: string }) {
  const [entries, setEntries] = useState<TimelineEntry[] | null>(null);
  const [partial, setPartial] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setError(null);

    fetchMerchantDebugContext(businessId, { limit: PREVIEW_LIMIT })
      .then((result) => {
        if (cancelled) return;
        setEntries(result.timeline);
        setPartial(result.partialSources);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  return (
    <BodySection
      title="Audit Log"
      footer={
        // Nothing more to read when the merge came back empty.
        entries && entries.length > 0 ? (
          <ViewAllLink to={merchantTimelinePath(businessId)}>
            View full timeline
          </ViewAllLink>
        ) : null
      }
    >
      {error ? (
        <p className="text-sm text-red-600">Could not load: {error}</p>
      ) : !entries ? (
        <p className="text-subtle-foreground text-sm">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing recorded yet.</p>
      ) : (
        <>
          {partial.length > 0 && (
            <p className="mb-3 text-sm text-amber-700">
              Could not load: {partial.join(", ")}. This is incomplete.
            </p>
          )}
          <MerchantTimeline entries={entries} showFilters={false} />
        </>
      )}
    </BodySection>
  );
}
